import { ApiResponse } from "@/api/types";
import { getDaysToToday } from "@/helpers/date-helper";
import { getAgentContract, getScout } from "@/grpc-web/scout/contract";
import { useStore } from "@/store";
import { Contract } from "@/components/Pages/Scout/elements/ContractPanelWrapper/ContractPanel/types";

export const checkAtsApproved = (contract: ApiResponse.Contract): boolean => {
  const atsPolicy = contract.policies.find(policy => {
    return policy.resource_name === "ats_approved";
  });
  if (!atsPolicy) {
    return false;
  }

  return atsPolicy.level >= 1;
};

export const checkPickUpApproved = (
  contract: ApiResponse.Contract
): boolean => {
  const pickUpPolicy = contract.policies.find(policy => {
    return policy.resource_name === "pick_up";
  });
  if (!pickUpPolicy) {
    return false;
  }

  return pickUpPolicy.level >= 1;
};

export const checkCalendarReviewApproved = (
  contract: ApiResponse.Contract
): boolean => {
  const calendarReviewPolicy = contract.policies.find(policy => {
    return policy.resource_name === "calendar_review_approved";
  });
  if (!calendarReviewPolicy) {
    return false;
  }

  return calendarReviewPolicy.level >= 1;
};

export const checkCheckinApproved = (
  contract: ApiResponse.Contract
): boolean => {
  const checkinPolicy = contract.policies.find(policy => {
    return policy.resource_name === "checkin_approved";
  });
  if (!checkinPolicy) {
    return false;
  }

  return checkinPolicy.level >= 1;
};

/**
 * 契約がイベント入稿可能な契約かどうかを判定する
 */
export const checkEventApproved = (contract: ApiResponse.Contract): boolean => {
  const eventPolicy = contract.policies.find(policy => {
    return policy.resource_name === "event_approved";
  });
  if (!eventPolicy) {
    return false;
  }

  return eventPolicy.level >= 1;
};

const PAID_JOB_CONTRACTS = ["ライトプラン", "スタンダードプラン"];
const PERFORMANCE_SCOUT_CONTRACTS = ["スカウト成果報酬プラン"];
const MONTHLY_SCOUT_CONTRACTS = ["スカウト定額プラン"];

/**
 * 求人掲載の契約のうち、一番早い開始日〜今日までの日数を返す
 */
export const getDaysFromJobContractStart = (
  contracts: ApiResponse.Contract[]
) => {
  const jobContracts = contracts.filter(contract =>
    PAID_JOB_CONTRACTS.includes(contract.plan)
  );
  // 契約がない場合は-1を返す
  if (!jobContracts.length) return -1;

  const earliestContract = jobContracts.reduce((earliest, current) =>
    current.start_at < earliest.start_at ? current : earliest
  );

  return getDaysToToday(earliestContract.start_at);
};

/**
 * スカウト成果報酬の契約のうち、一番早い開始日〜今日までの日数を返す
 */
export const getDaysFromPerformanceScoutContractStart = (
  contracts: ApiResponse.Contract[]
) => {
  const performanceScoutContracts = contracts.filter(contract =>
    PERFORMANCE_SCOUT_CONTRACTS.includes(contract.plan)
  );
  // 契約がない場合は-1を返す
  if (!performanceScoutContracts.length) return -1;

  const earliestContract = performanceScoutContracts.reduce(
    (earliest, current) =>
      current.start_at < earliest.start_at ? current : earliest
  );

  return getDaysToToday(earliestContract.start_at);
};

/**
 * スカウト月額の契約のうち、一番早い開始日〜今日までの日数を返す
 */
export const getDaysFromMonthlyScoutContractStart = (
  contracts: ApiResponse.Contract[]
) => {
  const monthlyScoutContracts = contracts.filter(contract =>
    MONTHLY_SCOUT_CONTRACTS.includes(contract.plan)
  );
  // 契約がない場合は-1を返す
  if (!monthlyScoutContracts.length) return -1;

  const earliestContract = monthlyScoutContracts.reduce((earliest, current) =>
    current.start_at < earliest.start_at ? current : earliest
  );

  return getDaysToToday(earliestContract.start_at);
};

/**
 * 契約が動画エントリー機能が有効かどうかを返す
 */
export const checkVideoBundleUnlockingLogsApproved = (
  contract: ApiResponse.Contract
): boolean => {
  const VideoBundleUnlockingLogsPolicy = contract.policies.find(policy => {
    return policy.resource_name === "video_bundle_unlocking_logs_approved";
  });

  if (!VideoBundleUnlockingLogsPolicy) {
    return false;
  }

  return VideoBundleUnlockingLogsPolicy.level >= 1;
};

export const hasScoutAgentContract = (
  contracts: ApiResponse.Contract[]
): boolean => {
  return contracts.some(contract => isScoutAgentContract(contract));
};

const isScoutAgentContract = (contract: ApiResponse.Contract): boolean => {
  const scoutAgentPolicy = contract.policies.find(policy => {
    return policy.resource_name === "scout_agent";
  });
  if (!scoutAgentPolicy) {
    return false;
  }

  return scoutAgentPolicy.level >= 1;
};

// スカウトエージェントメニューがアクティブ状態かどうかを判定する
const isScoutAgentMenuActive = () => {
  const store = useStore();
  return store.getters["domains/user/isScoutAgentMenuActive"];
};

// スカウト契約情報を取得してstoreに保存する
export const fetchAndStoreContract = async () => {
  const contract = await fetchScoutContract();

  const store = useStore();
  store.dispatch("domains/user/scoutPlan", contract);

  return contract;
};

const fetchScoutContract = async () => {
  if (isScoutAgentMenuActive()) {
    const response = await getAgentContract();

    return {
      scoutPlan: response.getPlan(),
      remainingTicketsCount: response.getRemainingTicketsCount(),
      expirationAtMs: response.hasExpirationAtMs()
        ? response.getExpirationAtMs()
        : undefined,
      scoutTargetGraduateYears: response.getTargetUserGraduateYearsList()
    } as Contract;
  }

  const response = await getScout();

  return {
    scoutPlan: response.getPlan(),
    remainingTicketsCount: response.getRemainingTicketsCount(),
    expirationAtMs: response.hasExpirationAtMs()
      ? response.getExpirationAtMs()
      : undefined,
    scoutTargetGraduateYears: response.getTargetUserGraduateYearsList()
  } as Contract;
};
